import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { TranslatorToolControl } from "../translator-tool-utils"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

export function TranslatorToolPanel({
  control,
  onClose,
}: {
  control: TranslatorToolControl
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [language, setLanguage] = useState("")
  const [error, setError] = useState<string | undefined>()

  const addLanguage = () => {
    const result = control.onAddLanguage(language)
    setError(result)
    if (!result) setLanguage("")
  }

  return (
    <div className="flex flex-col gap-4" data-testid="translator-tool-panel">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="translator-next-task">{t("translatorTool.nextTask")}</Label>
        <Switch
          checked={control.enabled}
          disabled={control.selectedLanguages.length === 0}
          id="translator-next-task"
          onCheckedChange={control.onEnabledChange}
        />
      </div>
      <p className="text-muted-foreground text-sm">{t("translatorTool.sourceHint")}</p>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-medium">{t("translatorTool.targetLanguages")}</legend>
        {control.availableLanguages.map((item) => {
          const checked = control.selectedLanguages.includes(item)
          return (
            <label
              key={item}
              className="hover:bg-accent flex min-h-10 items-center gap-3 rounded-md px-2 py-2"
            >
              <Checkbox
                checked={checked}
                disabled={
                  checked
                    ? control.selectedLanguages.length === 1
                    : control.selectedLanguages.length >= 10
                }
                onCheckedChange={(value) => control.onLanguageChange(item, value === true)}
              />
              <span className="min-w-0 text-sm break-words">{item}</span>
            </label>
          )
        })}
      </fieldset>
      <div className="flex flex-col gap-2">
        <Label htmlFor="translator-custom-language">{t("translatorTool.addLanguage")}</Label>
        <div className="flex items-center gap-2">
          <Input
            aria-invalid={Boolean(error)}
            id="translator-custom-language"
            maxLength={80}
            placeholder={t("translatorTool.languagePlaceholder")}
            value={language}
            onChange={(event) => {
              setLanguage(event.target.value)
              setError(undefined)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                addLanguage()
              }
            }}
          />
          <Button variant="outline" onClick={addLanguage}>
            {t("translatorTool.add")}
          </Button>
        </div>
        {error && (
          <p className="text-destructive-foreground text-sm" role="alert">
            {t(error === "duplicate" ? "translatorTool.duplicate" : "translatorTool.invalid")}
          </p>
        )}
      </div>
      <p className="text-muted-foreground text-sm">{t("translatorTool.multipleHint")}</p>
      <Button
        variant="outline"
        onClick={() => {
          onClose()
          control.onAdvanced()
        }}
      >
        {t("translatorTool.advanced", { language: control.glossaryLanguage })}
      </Button>
      <Button
        variant="ghost"
        onClick={() => {
          onClose()
          control.onHistory()
        }}
      >
        {t("translatorTool.history")}
      </Button>
      <Button onClick={onClose}>{t("translatorTool.done")}</Button>
    </div>
  )
}
